import { Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Button from '@/components/button'
import SelectInput from '@/components/filter-input'
import { useGetHerbicidesEconomy } from '@/hooks/queries/use-get-herbicides-economy'
import { routes } from '@/routes/config'
import { PeriodType } from '@/services/get-herbicides-economy'

import { EmptyComponent } from '../components/empty-component'
import Card from './components/card'
import { CustomBarChart } from './components/custom-bar-chart'
import CustomSkeleton from './components/custom-skeleton'
import { useStyles } from './styles'
import { getDateRange } from './utils/get-date-range'

const periodOptions = [
  { label: 'Últimos 15 dias', value: '15' },
  { label: 'Últimos 30 dias', value: '30' },
  { label: 'Últimos 60 dias', value: '60' },
  { label: 'Últimos 90 dias', value: '90' },
  { label: 'Últimos 120 dias', value: '120' },
]

export default function EconomyDashboard() {
  const classes = useStyles()
  const navigate = useNavigate()

  const [selectedValue, setSelectedValue] = useState(periodOptions[0].value)

  const { data, isLoading } = useGetHerbicidesEconomy(
    selectedValue as PeriodType,
  )

  const labels = periodOptions.map((option) => option.label)

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedLabel = event.target.value as string
    const selectedOption = periodOptions.find(
      (option) => option.label === selectedLabel,
    )
    setSelectedValue(selectedOption ? selectedOption.value : '')
  }

  const cardsData = useMemo(() => {
    if (!data) return []

    return [
      {
        title: `${data.economy_metrics.diagnoses_per_period} diagnósticos`,
        subtitle: 'no período selecionado',
        tooltipMessage:
          'Os diagnósticos considerados nos cálculos de economia são apenas os que, no período selecionado, possuem informações de tratamento cadastradas durante a personalização do mapa de pulverização.',
      },
      {
        title: `${data.economy_metrics.reduction_percentage_herbicides}%`,
        subtitle: 'de redução no uso de herbicidas',
        tooltipMessage:
          'A redução no uso de herbicidas é calculada dividindo a economia total de herbicidas pelo custo total dos tratamentos SEM Cromai.',
      },
      {
        title: `R$ ${data.economy_metrics.reduction_in_herbicides}`,
        subtitle: 'economizados em herbicidas',
        tooltipMessage:
          'A economia de herbicidas é calculada subtraindo o custo total dos tratamentos COM Cromai do custo total dos tratamentos SEM Cromai.',
      },
      {
        title: `R$ ${data.economy_metrics.herbicides_per_ha}`,
        subtitle: 'economizados em herbicidas por ha',
        tooltipMessage:
          'A economia por hectare é calculada dividindo a economia total pela área total dos diagnósticos.',
      },
    ]
  }, [data])

  return (
    <>
      <header className={classes.header}>
        <div>
          <Typography variant='h1'>Dashboard de economia</Typography>
          <Typography variant='h3'>
            {getDateRange(Number(selectedValue))}
          </Typography>
        </div>
        <SelectInput
          label='Período:'
          options={labels}
          value={
            periodOptions.find((option) => option.value === selectedValue)
              ?.label || ''
          }
          name='period'
          onChange={handleChange}
        />
      </header>
      {isLoading ? (
        <CustomSkeleton />
      ) : !data ? (
        <EmptyComponent
          title='Nenhum diagnóstico disponível no período'
          subtitle='Não há nenhum diagnóstico contendo seus indicadores de economia disponível. Para visualizar os dados cadastre seus tratamentos e insira essa informação na personalização do mapa de pulverização.'
          button={
            <Button onClick={() => navigate(routes.Reports('operation-costs'))}>
              <AddIcon /> Cadastrar tratamentos
            </Button>
          }
        />
      ) : (
        <>
          <div className={classes.wrapperCards}>
            {cardsData.map((card, index) => (
              <Card
                key={index}
                title={card.title}
                subtitle={card.subtitle}
                tooltipMessage={card.tooltipMessage}
              />
            ))}
          </div>

          <div className={classes.wrapperCharts}>
            <CustomBarChart
              chartType='total'
              data={data?.total_herbicides_per_period}
              title='Gastos totais em herbicidas'
              tooltipMessage='Gastos totais em herbicidas no período selecionado.'
            />
            <CustomBarChart
              chartType='diagnosis'
              data={data?.recent_diagnoses_spending}
              title='Gastos em herbicidas nos últimos 10 diagnósticos'
              tooltipMessage='Gastos em herbicidas nos últimos 10 diagnósticos com informações de tratamento cadastradas durante a criação do mapa de pulverização.'
            />
          </div>
        </>
      )}
    </>
  )
}
